import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { apiGet, apiPost } from '../services/api';
import LoadingSpinnerAdvanced from '../components/LoadingSpinnerAdvanced';
import GerenciarAvisos from '../components/GerenciarAvisos';
import AcessosPorUsuario from '../components/AcessosPorUsuario';
import AuditoriaAcessos from '../components/AuditoriaAcessos';
import LogosPrefeituras from '../components/LogosPrefeituras';
import VisaoGeral from '../components/configuracoes/VisaoGeral';
import DadosDosAlunos from '../components/configuracoes/DadosDosAlunos';
import { useEffectiveCity } from '../hooks/useEffectiveCity';

function Configuracoes() {
  const { user } = useAuth();
  const { darkMode } = useTheme();
  // Visão Geral do município escolhido no menu (sem escolha, da rede).
  const cidade = useEffectiveCity();

  const [stats, setStats] = useState(null);
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupMessage, setBackupMessage] = useState(null);
  // Diagnóstico de e-mail. Sem ele, descobrir por que uma redefinição de
  // senha não chegou exige console do navegador ou SSH no servidor — e o
  // erro que importa (credencial errada, remetente não verificado, conta
  // ainda no sandbox do SES) só aparece no log do backend.
  const [emailTo, setEmailTo] = useState('');
  const [emailTesting, setEmailTesting] = useState(false);
  const [emailResult, setEmailResult] = useState(null);
  const [activeTab, setActiveTab] = useState('visao-geral');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const statsData = await apiGet('/system-stats/', cidade?.id ? { city: cidade.id } : {}).catch(() => null);
      if (statsData) setStats(statsData);

      if (user?.role === 'administrador') {
        const backupsData = await apiGet('/backups/').catch(() => ({ backups: [] }));
        setBackups(backupsData.backups || []);
      }
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
    } finally {
      setLoading(false);
    }
  }, [user, cidade?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Botão "Atualizar" da Visão Geral: busca os números de novo sem trocar a
  // tela pelo carregamento de página inteira.
  const [atualizando, setAtualizando] = useState(false);
  const atualizarStats = useCallback(async () => {
    setAtualizando(true);
    try {
      const statsData = await apiGet('/system-stats/', cidade?.id ? { city: cidade.id } : {}).catch(() => null);
      if (statsData) setStats(statsData);
    } finally {
      setAtualizando(false);
    }
  }, [cidade?.id]);

  const handleTestEmail = async () => {
    const destino = emailTo.trim() || user?.email || '';
    if (!destino) {
      setEmailResult({ ok: false, texto: 'Informe um endereço de destino.' });
      return;
    }
    setEmailTesting(true);
    setEmailResult(null);
    try {
      const r = await apiPost('/admin/test-email/', { to: destino });
      setEmailResult({
        ok: true,
        texto: `E-mail aceito pelo servidor e enviado para ${r.to}.`,
        config: r.config,
      });
    } catch (err) {
      console.error('Falha no teste de e-mail:', err);
      const d = err.response?.data || {};
      setEmailResult({
        ok: false,
        // O tipo e o detalhe do erro são a resposta: "não verificado" é
        // conta no sandbox, "authentication failed" é credencial SMTP.
        texto: [d.error_type, d.error_detail].filter(Boolean).join(': ')
          || err.message || 'Não foi possível enviar.',
        config: d.config,
      });
    } finally {
      setEmailTesting(false);
    }
  };

  const handleBackup = async () => {
    setBackupLoading(true);
    setBackupMessage(null);
    try {
      const result = await apiPost('/backup/', {});
      setBackupMessage({ type: 'success', text: `Backup criado com sucesso: ${result.filename}` });
      if (result.download_url) {
        window.open(result.download_url, '_blank');
      }
      fetchData();
    } catch (err) {
      setBackupMessage({ type: 'error', text: err.message || 'Erro ao criar backup' });
    } finally {
      setBackupLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const roleLabels = {
    administrador: 'Administrador',
    coordenador: 'Coordenador',
    supervisor: 'Supervisor',
    gestor: 'Gestor',
  };

  const cardClass = `rounded-xl p-6 border ${darkMode ? 'bg-slate-800/70 border-slate-700/10' : 'bg-white/90 border-slate-200 shadow-sm'}`;
  const titleClass = `text-lg font-semibold mb-4 ${darkMode ? 'text-sideb-green' : 'text-[#F9B047]'}`;
  const textClass = darkMode ? 'text-slate-300' : 'text-slate-600';
  const mutedClass = darkMode ? 'text-slate-400' : 'text-slate-500';
  const labelClass = `block text-sm mb-1 ${mutedClass}`;

  const tabs = [
    { id: 'visao-geral', label: 'Visão Geral' },
    { id: 'logs', label: 'Auditoria' },
    { id: 'backup', label: 'Backup & Dados' },
    { id: 'dados-alunos', label: 'Dados dos Alunos' },
    { id: 'avisos', label: 'Avisos' },
    { id: 'acessos', label: 'Acessos por Usuário' },
    { id: 'logomarcas', label: 'Logomarcas' },
    { id: 'permissoes', label: 'Permissões' },
  ];

  if (loading) {
    return (
      <LoadingSpinnerAdvanced
        darkMode={darkMode}
        message="Carregando Configurações..."
        subtitle="Buscando as preferências do sistema..."
        size="xl"
      />
    );
  }

  return (
    <>
      <header className="flex flex-wrap gap-4 justify-between items-start mb-6 md:mb-8">
        <div>
          <h1 className={`text-2xl md:text-3xl font-medium bg-clip-text text-transparent ${darkMode
            ? 'bg-gradient-to-r from-gray-50 to-gray-400'
            : 'bg-gradient-to-r from-[#0974BA] to-[#2E4974]'
          }`}>
            Configurações do Sistema
          </h1>
          <p className={`mt-2 text-sm ${mutedClass}`}>
            Painel gerencial do SIDEB
          </p>
        </div>
      </header>

      {/* Tabs */}
      <div className={`flex flex-wrap gap-1 mb-6 p-1 rounded-lg ${darkMode ? 'bg-slate-800/50' : 'bg-slate-100'}`}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? darkMode
                  ? 'bg-sideb-green text-white'
                  : 'bg-white text-[#F9B047] shadow-sm'
                : darkMode
                  ? 'text-slate-400 hover:text-slate-200'
                  : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ===== VISAO GERAL ===== */}
      {activeTab === 'visao-geral' && (
        <VisaoGeral stats={stats} cidadeId={cidade?.id} cidadeNome={cidade?.name} darkMode={darkMode}
          onAtualizar={atualizarStats} atualizando={atualizando} />
      )}

      {/* ===== AUDITORIA ===== */}
      {activeTab === 'logs' && (
        <AuditoriaAcessos
          darkMode={darkMode}
          cardClass={cardClass}
          titleClass={titleClass}
          mutedClass={mutedClass}
        />
      )}

      {/* ===== DADOS DOS ALUNOS (ficha completa, só admin) ===== */}
      {activeTab === 'dados-alunos' && (
        <DadosDosAlunos
          darkMode={darkMode}
          cardClass={cardClass}
          titleClass={titleClass}
          mutedClass={mutedClass}
        />
      )}

      {/* ===== AVISOS ===== */}
      {activeTab === 'avisos' && (
        <GerenciarAvisos
          darkMode={darkMode}
          cardClass={cardClass}
          titleClass={titleClass}
          mutedClass={mutedClass}
        />
      )}

      {/* ===== ACESSOS POR USUÁRIO ===== */}
      {activeTab === 'acessos' && (
        <AcessosPorUsuario
          darkMode={darkMode}
          cardClass={cardClass}
          titleClass={titleClass}
          mutedClass={mutedClass}
        />
      )}

      {/* ===== LOGOMARCAS ===== */}
      {activeTab === 'logomarcas' && (
        <LogosPrefeituras
          darkMode={darkMode}
          cardClass={cardClass}
          titleClass={titleClass}
          mutedClass={mutedClass}
        />
      )}

      {/* ===== BACKUP & DADOS ===== */}
      {activeTab === 'backup' && (
        <div className="space-y-6">
          {/* Diagnóstico de e-mail: é daqui que sai a resposta quando uma
              redefinição de senha "não chegou". O importante é a mensagem
              crua do servidor — ela nomeia a causa. */}
          <div className={cardClass}>
            <h2 className={titleClass}>Envio de E-mail</h2>
            <p className={`text-sm mb-4 ${mutedClass}`}>
              Manda um e-mail de teste e mostra a configuração em uso. Use
              quando uma redefinição de senha ou um convite não chegar — a
              resposta diz se o problema é a credencial, o remetente ou o
              destinatário.
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                placeholder={user?.email || 'destino@exemplo.com'}
                className={`flex-1 min-w-0 px-4 py-2.5 rounded-lg text-sm border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${
                  darkMode
                    ? 'bg-slate-700/50 border-slate-600 text-slate-200 placeholder-slate-500'
                    : 'bg-slate-50 border-slate-200 text-slate-700 placeholder-slate-400'
                }`}
              />
              <button
                onClick={handleTestEmail}
                disabled={emailTesting || user?.role !== 'administrador'}
                className={`px-6 py-2.5 rounded-lg font-medium text-sm whitespace-nowrap transition-colors ${
                  user?.role === 'administrador' && !emailTesting
                    ? 'bg-[#0974BA] hover:bg-[#0974BA]/80 text-white'
                    : darkMode
                      ? 'bg-slate-700 text-slate-500 cursor-not-allowed'
                      : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                }`}
              >
                {emailTesting ? 'Enviando...' : 'Enviar teste'}
              </button>
            </div>

            {user?.role !== 'administrador' && (
              <p className={`mt-2 text-xs ${mutedClass}`}>
                Apenas administradores podem testar o envio.
              </p>
            )}

            {emailResult && (
              <div className={`mt-4 p-3 rounded-lg text-sm ${
                emailResult.ok
                  ? darkMode ? 'bg-[#13983D]/20 text-[#13983D] border border-[#13983D]/50' : 'bg-[#13983D]/5 text-[#13983D] border border-[#13983D]/20'
                  : darkMode ? 'bg-[#E83A3C]/20 text-[#E83A3C] border border-[#E83A3C]/50' : 'bg-[#E83A3C]/5 text-[#E83A3C] border border-[#E83A3C]/20'
              }`}>
                <p className="break-words">{emailResult.texto}</p>
                {emailResult.ok && (
                  <p className="mt-1 text-xs opacity-80">
                    Aceito não é entregue: confira a caixa de entrada e o spam.
                  </p>
                )}
              </div>
            )}

            {emailResult?.config && (
              <div className={`mt-3 p-3 rounded-lg text-xs font-mono overflow-x-auto ${
                darkMode ? 'bg-slate-900/60 text-slate-300' : 'bg-slate-50 text-slate-600'
              }`}>
                <div>servidor: {emailResult.config.EMAIL_HOST}:{emailResult.config.EMAIL_PORT}</div>
                <div>remetente: {emailResult.config.DEFAULT_FROM_EMAIL}</div>
                <div>
                  usuário: {emailResult.config.EMAIL_HOST_USER_set ? 'definido' : 'NÃO DEFINIDO'}
                  {' · '}
                  senha: {emailResult.config.EMAIL_HOST_PASSWORD_set ? 'definida' : 'NÃO DEFINIDA'}
                </div>
              </div>
            )}
          </div>

          <div className={cardClass}>
            <h2 className={titleClass}>Backup do Banco de Dados</h2>
            <p className={`text-sm mb-4 ${mutedClass}`}>
              Gere um backup completo do banco de dados. O arquivo sera salvo no S3 e disponibilizado para download.
            </p>

            {stats?.last_backup && (
              <div className={`mb-4 p-3 rounded-lg ${darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                <p className={`text-sm ${textClass}`}>
                  <span className="font-medium">Último backup:</span> {formatDate(stats.last_backup.created_at)}
                </p>
                {stats.last_backup.description && (
                  <p className={`text-xs mt-1 ${mutedClass}`}>{stats.last_backup.description}</p>
                )}
              </div>
            )}

            {backupMessage && (
              <div className={`mb-4 p-3 rounded-lg text-sm ${
                backupMessage.type === 'success'
                  ? darkMode ? 'bg-[#13983D]/20 text-[#13983D] border border-[#13983D]/50' : 'bg-[#13983D]/5 text-[#13983D] border border-[#13983D]/20'
                  : darkMode ? 'bg-[#E83A3C]/20 text-[#E83A3C] border border-[#E83A3C]/50' : 'bg-[#E83A3C]/5 text-[#E83A3C] border border-[#E83A3C]/20'
              }`}>
                {backupMessage.text}
              </div>
            )}

            <div className="flex space-x-3">
              <button
                onClick={handleBackup}
                disabled={backupLoading || user?.role !== 'administrador'}
                className={`px-6 py-2.5 rounded-lg font-medium text-sm transition-colors ${
                  user?.role === 'administrador'
                    ? 'bg-sideb-green hover:bg-sideb-green/80 text-white'
                    : darkMode
                      ? 'bg-slate-700 text-slate-500 cursor-not-allowed'
                      : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                }`}
              >
                {backupLoading ? (
                  <span className="flex items-center space-x-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                    <span>Gerando backup...</span>
                  </span>
                ) : 'Gerar Backup Agora'}
              </button>
            </div>

            {user?.role !== 'administrador' && (
              <p className={`mt-2 text-xs ${mutedClass}`}>
                Apenas administradores podem gerar backups.
              </p>
            )}
          </div>

          {/* Lista de Backups */}
          {user?.role === 'administrador' && (
            <div className={cardClass}>
              <h2 className={titleClass}>Backups Disponiveis</h2>
              {backups.length === 0 ? (
                <p className={mutedClass}>Nenhum backup encontrado no S3.</p>
              ) : (
                <div className="space-y-2">
                  {backups.map((bk, i) => (
                    <div key={i} className={`flex items-center justify-between p-3 rounded-lg ${darkMode ? 'bg-slate-700/50 hover:bg-slate-700' : 'bg-slate-50 hover:bg-slate-100'} transition-colors`}>
                      <div>
                        <p className={`text-sm font-medium ${textClass}`}>{bk.filename}</p>
                        <p className={`text-xs ${mutedClass}`}>
                          {formatDate(bk.last_modified)} - {formatFileSize(bk.size)}
                        </p>
                      </div>
                      <a
                        href={bk.download_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 rounded-lg text-sm bg-sideb-green hover:bg-sideb-green/80 text-white transition-colors"
                      >
                        Download
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ===== PERMISSOES ===== */}
      {activeTab === 'permissoes' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className={cardClass}>
            <h2 className={titleClass}>Hierarquia de Permissões</h2>
            <div className="space-y-4">
              {[
                {
                  role: 'Administrador',
                  desc: 'Acesso total ao sistema, todas as cidades e escolas',
                  perms: ['Dashboard', 'Usuários', 'Escolas', 'Provas', 'Relatórios', 'Configurações', 'Backup'],
                  color: darkMode ? 'border-[#E83A3C]/50 bg-[#E83A3C]/10' : 'border-[#E83A3C]/20 bg-[#E83A3C]/5',
                },
                {
                  role: 'Coordenador',
                  desc: 'Acesso completo aos dados da sua cidade',
                  perms: ['Dashboard', 'Escolas', 'Provas', 'Relatórios', 'Turmas', 'Alunos'],
                  color: darkMode ? 'border-[#0974BA]/50 bg-[#0974BA]/10' : 'border-[#0974BA]/20 bg-[#0974BA]/5',
                },
                {
                  role: 'Supervisor',
                  desc: 'Acesso apenas às escolas atribuídas pelo coordenador',
                  perms: ['Dashboard', 'Escolas Atribuídas', 'Turmas', 'Alunos', 'Provas'],
                  color: darkMode ? 'border-[#F9B047]/50 bg-[#F9B047]/10' : 'border-[#F9B047]/20 bg-[#F9B047]/5',
                },
                {
                  role: 'Gestor',
                  desc: 'Apenas visualiza dashboard e relatórios (somente leitura)',
                  perms: ['Dashboard', 'Relatórios'],
                  color: darkMode ? 'border-[#13983D]/50 bg-[#13983D]/10' : 'border-[#13983D]/20 bg-[#13983D]/5',
                },
              ].map((r, i) => (
                <div key={i} className={`p-4 rounded-lg border ${r.color}`}>
                  <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-slate-800'}`}>{r.role}</h3>
                  <p className={`text-xs mt-1 ${mutedClass}`}>{r.desc}</p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {r.perms.map((p, j) => (
                      <span key={j} className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-slate-700 text-slate-300' : 'bg-white text-slate-600 border border-slate-200'}`}>
                        {p}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className={cardClass}>
            <h2 className={titleClass}>Informações do Sistema</h2>
            <div className="space-y-3">
              {[
                { label: 'Sistema', value: 'SIDEB - Sistema de Indicadores de Educação Básica' },
                { label: 'Seu Usuário', value: user?.username || '-' },
                { label: 'Seu Perfil', value: roleLabels[user?.role] || user?.role || '-' },
                { label: 'Email', value: user?.email || '-' },
                { label: 'Último Login', value: user?.last_login ? formatDate(user.last_login) : '-' },
              ].map((item, i) => (
                <div key={i} className={`flex justify-between py-2 border-b last:border-0 ${darkMode ? 'border-slate-700' : 'border-slate-100'}`}>
                  <span className={labelClass}>{item.label}</span>
                  <span className={`text-sm ${textClass}`}>{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Configuracoes;
